import { Link } from "react-router-dom";

// A sorted horizontal bar list. Used for the two AI breakdowns — what leads
// were worried about, and which treatments they asked about.
//
// One colour, because both charts show a single measure across categories: the
// bar length is the whole encoding, so a second hue would carry no meaning.
// Every bar is directly labelled, so identity never rests on colour alone and
// no legend is needed.
const BarListChart = ({
  title,
  subtitle,
  data = [],
  emptyMessage,
  emptyHint,
  linkFor,
  formatSecondary,
  caveat,
}) => {
  const max = data.reduce((highest, row) => Math.max(highest, row.value), 0);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mb-5">{subtitle}</p>}

      {data.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500 text-sm">{emptyMessage}</p>
          {emptyHint && (
            <p className="text-gray-400 text-xs mt-1">{emptyHint}</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {data.map((row) => {
            const width =
              row.value === 0 || max === 0
                ? 0
                : Math.max((row.value / max) * 100, 2);
            const label = row.label || row.name;
            const secondary = formatSecondary ? formatSecondary(row) : null;
            const to = linkFor ? linkFor(row) : null;

            const body = (
              <>
                <span className="w-28 md:w-44 flex-shrink-0 text-sm text-gray-600 md:truncate text-right" title={label}>
                  {label}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="block h-6 rounded-r bg-teal-700 group-hover:bg-teal-800 transition-colors"
                    style={{ width: `${width}%` }}
                  />
                </span>
                <span className="w-10 flex-shrink-0 text-sm font-semibold text-gray-800 text-right">
                  {row.value}
                </span>
                {secondary && (
                  <span className="w-16 md:w-20 flex-shrink-0 text-xs text-gray-500 text-right">
                    {secondary}
                  </span>
                )}
              </>
            );

            return to ? (
              <Link
                key={row.name}
                to={to}
                className="group flex items-center gap-2 md:gap-3"
              >
                {body}
              </Link>
            ) : (
              <div key={row.name} className="group flex items-center gap-2 md:gap-3">
                {body}
              </div>
            );
          })}
        </div>
      )}

      {caveat && data.length > 0 && (
        <p className="text-xs text-gray-400 mt-4 pt-3 border-t border-border">
          {caveat}
        </p>
      )}
    </div>
  );
};

export default BarListChart;
